/**
 * Notification Process
 * 
 * System process that spawns toast notifications.
 * Toast views don't conform to grid rules - they use fixed positioning.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority, ProcessLifecycleMode } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { 
  designTokens, 
  createButton,
  createContainer 
} from '../../core/design-system.js';
import { dismissNotification as dismissServerNotification } from '../../services/notifications/api.js';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number; // Auto-dismiss after milliseconds (0 = no auto-dismiss)
  action?: {
    label: string;
    onClick: () => void;
  };
  serverNotificationId?: string;
}

export class NotificationProcess extends BaseProcess {
  kind: string = 'notifications';

  metadata = {
    priority: ProcessPriority.SYSTEM,
    category: 'system' as const,
    canStop: false,
    preserveState: false,
    timeSensitive: false
  };

  private notifications: Map<string, NotificationView> = new Map();
  
  // Cache of active notification messages to prevent duplicates
  // Key: normalized message string, Value: notification ID
  private activeNotificationCache: Map<string, string> = new Map();
  
  // Cache of recently dismissed notifications (to prevent immediate re-showing)
  // Key: normalized message string, Value: timestamp when dismissed
  private recentlyDismissedCache: Map<string, number> = new Map();
  private readonly DISMISS_CACHE_DURATION = 2000; // 2 seconds - don't re-show same notification within 2s

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[NotificationProcess] Starting notification process ${this.id}`);
    
    // Listen for notification events
    bus.on('notification.show', (payload: any) => {
      const { type, message, duration, action, serverNotificationId } = payload;
      this.showNotification(ctx, type, message, duration, action, serverNotificationId);
    });
    
    bus.on('notification.dismiss', (payload: any) => {
      const { id } = payload;
      this.dismissNotification(ctx, id);
    });
    
    // Clean up recently dismissed cache periodically
    setInterval(() => {
      this.cleanupDismissedCache();
    }, 5000); // Clean every 5 seconds
  }
  
  /**
   * Normalize a notification message for deduplication
   * Removes extra whitespace and converts to lowercase for comparison
   */
  private normalizeMessage(message: string): string {
    return message.trim().toLowerCase().replace(/\s+/g, ' ');
  }
  
  /**
   * Check if a notification with the same message is already showing
   */
  private isDuplicate(message: string): boolean {
    const normalized = this.normalizeMessage(message);
    
    // Check if already showing
    if (this.activeNotificationCache.has(normalized)) {
      const existingId = this.activeNotificationCache.get(normalized);
      const existingView = this.notifications.get(existingId!);
      if (existingView) {
        log.debug(`[NotificationProcess] Duplicate notification prevented: "${message}"`);
        return true;
      } else {
        // Clean up stale cache entry
        this.activeNotificationCache.delete(normalized);
      }
    }
    
    // Check if recently dismissed (within cache duration)
    if (this.recentlyDismissedCache.has(normalized)) {
      const dismissedTime = this.recentlyDismissedCache.get(normalized)!;
      const timeSinceDismissed = Date.now() - dismissedTime;
      if (timeSinceDismissed < this.DISMISS_CACHE_DURATION) {
        log.debug(`[NotificationProcess] Recently dismissed notification prevented: "${message}" (${timeSinceDismissed}ms ago)`);
        return true;
      } else {
        // Clean up expired cache entry
        this.recentlyDismissedCache.delete(normalized);
      }
    }
    
    return false;
  }
  
  /**
   * Clean up expired entries from recently dismissed cache
   */
  private cleanupDismissedCache(): void {
    const now = Date.now();
    for (const [message, dismissedTime] of this.recentlyDismissedCache.entries()) {
      if (now - dismissedTime >= this.DISMISS_CACHE_DURATION) {
        this.recentlyDismissedCache.delete(message);
      }
    }
  }

  private showNotification(
    ctx: ProcessContext,
    type: NotificationType,
    message: string,
    duration?: number,
    action?: { label: string; onClick: () => void },
    serverNotificationId?: string
  ): void {
    // Check for duplicates before showing
    if (this.isDuplicate(message)) {
      return; // Don't show duplicate notification
    }
    
    const notificationId = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const normalizedMessage = this.normalizeMessage(message);
    
    // Add to active cache
    this.activeNotificationCache.set(normalizedMessage, notificationId);
    
    const view = new NotificationView(
      this.id,
      notificationId,
      type,
      message,
      duration,
      action,
      this,
      ctx,
      normalizedMessage, // Pass normalized message for cleanup
      serverNotificationId
    );
    const viewInstance = view.toViewInstance();
    this.notifications.set(notificationId, view);
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
    
    log.debug(`[NotificationProcess] Notification ${notificationId} spawned: "${message}"`);
  }

  dismissNotification(ctx: ProcessContext, id: string): void {
    const view = this.notifications.get(id);
    if (view) {
      // Get normalized message from view for cache cleanup
      const normalizedMessage = (view as any).normalizedMessage;
      
      // Remove from active cache
      if (normalizedMessage) {
        this.activeNotificationCache.delete(normalizedMessage);
        // Add to recently dismissed cache
        this.recentlyDismissedCache.set(normalizedMessage, Date.now());
      }
      
      ctx.destroyView(view.id);
      this.views.delete(view.id);
      this.notifications.delete(id);
      
      log.debug(`[NotificationProcess] Notification ${id} dismissed`);
    }
  }
}

/**
 * Notification Toast View
 * 
 * Fixed positioning, doesn't conform to grid rules.
 */
class NotificationView extends BaseViewHTML {
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;
  public normalizedMessage: string; // Store normalized message for cache cleanup
  private serverNotificationId?: string;

  constructor(
    processId: string,
    viewId: string,
    private type: NotificationType,
    private message: string,
    private duration: number | undefined,
    private action: { label: string; onClick: () => void } | undefined,
    private notificationProcess: NotificationProcess,
    private ctx: ProcessContext,
    normalizedMessage: string,
    serverNotificationId?: string
  ) {
    super(processId, viewId, {
      sizes: [0], // Half height, normal width (compact notification)
      showDismissButton: true,
      resizable: false
    });
    this.normalizedMessage = normalizedMessage;
    this.serverNotificationId = serverNotificationId;
  }

  protected getContentHTML(): string {
    const typeColors = {
      success: 'rgba(0, 200, 0, 0.2)',
      error: 'rgba(255, 0, 0, 0.2)',
      warning: 'rgba(255, 200, 0, 0.2)',
      info: 'rgba(102, 204, 255, 0.2)'
    };
    
    const typeBorders = {
      success: 'rgba(0, 200, 0, 0.4)',
      error: 'rgba(255, 0, 0, 0.4)',
      warning: 'rgba(255, 200, 0, 0.4)',
      info: 'rgba(102, 204, 255, 0.4)'
    };
    
    const actionButton = this.action 
      ? createButton(this.action.label, {
          className: 'notification-action',
          variant: 'primary'
        })
      : '';
    
    return `
      <div style="
        display: flex;
        align-items: center;
        gap: ${designTokens.padding.md};
        width: 100%;
        height: 100%;
      ">
        <div style="
          width: 4px;
          height: 100%;
          background: ${typeBorders[this.type]};
          border-radius: ${designTokens.radius.sm} 0 0 ${designTokens.radius.sm};
          flex-shrink: 0;
        "></div>
        <div style="
          flex: 1;
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColor};
          line-height: 1.4;
        ">${this.escapeHtml(this.message)}</div>
        ${actionButton ? `<div style="flex-shrink: 0;">${actionButton}</div>` : ''}
      </div>
    `;
  }

  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Apply notification-specific styling (fixed positioning, no grid)
    this.element.style.position = 'fixed';
    this.element.style.top = '20px';
    this.element.style.right = '20px';
    this.element.style.zIndex = '10001'; // Above AI visualization
    this.element.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
    
    // Setup action button
    if (this.action) {
      const actionButton = this.element.querySelector('.notification-action') as HTMLButtonElement;
      if (actionButton) {
        actionButton.addEventListener('click', () => {
          this.action!.onClick();
          this.dismiss();
        });
      }
    }
    
    // Setup auto-dismiss
    if (this.duration && this.duration > 0) {
      this.autoDismissTimer = setTimeout(() => {
        this.dismiss();
      }, this.duration);
    }
  }

  private dismiss(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
    }
    if (this.serverNotificationId) {
      dismissServerNotification(this.serverNotificationId).catch((error) => {
        console.warn('[NotificationProcess] Failed to sync dismissal', error);
      });
    }
    bus.emit('notification.dismiss', { id: this.id });
  }

  unmount(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
    }
    super.unmount();
  }
}

registerProcess('notifications', (id: string) => new NotificationProcess(id));

